import { useState } from 'react'
import { AvatarImage } from '../../../components/avatar-image'
import type { CallParticipant } from '../../../types/voice-call'

type ParticipantRosterPanelProps = {
  participants: CallParticipant[]
  currentUserId?: string
  isHost: boolean
  atCap: boolean
  liveCount: number
  maxParticipants: number
  selfMuted: boolean
  onAddPeople: () => void
  onAskToMute: (userId: string) => void
  onRemove: (userId: string) => void
  onReAdd: (participant: CallParticipant) => void
}

function MicOffBadge() {
  return (
    <svg className="h-4 w-4 text-white/70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M1 1l22 22M9 9v3a3 3 0 005.12 2.12M15 9.34V5a3 3 0 00-5.94-.6" />
      <path d="M12 19v3M8 22h8M12 15a3 3 0 01-3-3V5a3 3 0 016 0v7" />
    </svg>
  )
}

function stateLabel(state: CallParticipant['state']): string {
  switch (state) {
    case 'ringing':
      return 'Ringing…'
    case 'invited':
      return 'Invited'
    case 'active':
      return 'In call'
    case 'left':
      return 'Left'
    case 'declined':
      return 'Declined'
    default:
      return state
  }
}

export function ParticipantRosterPanel({
  participants,
  currentUserId,
  isHost,
  atCap,
  liveCount,
  maxParticipants,
  selfMuted,
  onAddPeople,
  onAskToMute,
  onRemove,
  onReAdd,
}: ParticipantRosterPanelProps) {
  const [menuFor, setMenuFor] = useState<string | null>(null)
  const live = participants.filter((p) => p.state !== 'left' && p.state !== 'declined')
  const gone = participants.filter((p) => p.state === 'left' || p.state === 'declined')

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-white/10 px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-white/60">
          {liveCount}/{maxParticipants} in call
        </span>
        {isHost && (
          <button
            type="button"
            onClick={onAddPeople}
            disabled={atCap}
            className="rounded-lg bg-white/10 px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-white/20 disabled:opacity-40"
            aria-label={atCap ? `Call is full (${maxParticipants})` : 'Add people to the call'}
          >
            + Add
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
        <ul className="space-y-1" role="list">
          {live.map((p) => {
            const isSelf = p.userId === currentUserId
            const muted = isSelf ? selfMuted : Boolean(p.mutedSelf)
            return (
              <li
                key={p.userId}
                className={`relative flex items-center gap-2 rounded-lg px-2 py-1.5 ${
                  p.speaking ? 'bg-emerald-500/15 ring-1 ring-emerald-400/50' : 'hover:bg-white/5'
                }`}
              >
                <AvatarImage
                  name={p.displayName}
                  avatarUrl={p.avatarUrl}
                  size={32}
                  variant="provider"
                  fallback="logo"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-white">
                    {isSelf ? 'You' : p.displayName}
                    {p.role === 'host' ? ' · host' : ''}
                  </p>
                  <p className="text-[11px] text-white/50">{stateLabel(p.state)}</p>
                </div>
                {muted && <MicOffBadge />}
                {isHost && !isSelf && p.state === 'active' && (
                  <div className="relative shrink-0">
                    <button
                      type="button"
                      onClick={() => setMenuFor(menuFor === p.userId ? null : p.userId)}
                      className="rounded-lg px-2 py-1 text-white/70 hover:bg-white/10"
                      aria-label={`Manage ${p.displayName}`}
                      aria-haspopup="menu"
                      aria-expanded={menuFor === p.userId}
                    >
                      ⋯
                    </button>
                    {menuFor === p.userId && (
                      <div
                        className="absolute right-0 top-9 z-10 w-36 overflow-hidden rounded-lg bg-white text-sm text-slate-800 shadow-xl"
                        role="menu"
                      >
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            onAskToMute(p.userId)
                            setMenuFor(null)
                          }}
                          className="block w-full px-3 py-2 text-left hover:bg-slate-100"
                        >
                          Ask to mute
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            onRemove(p.userId)
                            setMenuFor(null)
                          }}
                          className="block w-full px-3 py-2 text-left text-red-600 hover:bg-red-50"
                        >
                          Remove from call
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>

        {gone.length > 0 && (
          <div className="mt-3">
            <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-white/40">
              Not in call
            </p>
            <ul className="space-y-1" role="list">
              {gone.map((p) => (
                <li key={p.userId} className="flex items-center gap-2 rounded-lg px-2 py-1.5">
                  <AvatarImage
                    name={p.displayName}
                    avatarUrl={p.avatarUrl}
                    size={32}
                    variant="neutral"
                    fallback="logo"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-white/70">{p.displayName}</p>
                    <p className="text-[11px] text-white/40">{stateLabel(p.state)}</p>
                  </div>
                  {isHost && (
                    <button
                      type="button"
                      onClick={() => onReAdd(p)}
                      disabled={atCap}
                      className="shrink-0 rounded-lg bg-white/10 px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-white/20 disabled:opacity-40"
                    >
                      Re-add
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
